// Import necessary modules from Redux Toolkit
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL } from '../../api/constants';
import { axiosPrivate } from '../../api/axios';

// Initial state for the DICOM logs slice
const initialState = {
  dicomLogs: [],
  loading: false,
  selectedLogId: null,
  error: null,
  isDicomEventLoading: false,
  dicomEventError: null,
  dicomEventDetails: null,
  isDicomEventUnlocked: false,
};

// Async thunk to fetch DICOM logs data
export const fetchDicomLogs = createAsyncThunk(
  'dicomLogs/fetchDicomLogs',
  async (
    { axiosInstance = axiosPrivate, path = `${API_URL.DICOM_SENDER_LOGS}?pageNo=0&pageSize=250` },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(path);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch DICOM logs');
    }
  }
);

// Helper function to handle API errors
const handleApiError = (error, logId) => ({
  message: error.response?.data || 'Failed to fetch DICOM event details',
  logId,
  status: error.response?.status || 500,
  isUnlocked: error.response?.data?.isUnlocked || false,
});

// Async thunk to fetch DICOM event details
export const fetchDicomEventDetails = createAsyncThunk(
  'dicomLogs/fetchDicomEventDetails',
  async ({ axiosInstance = axiosPrivate, logId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_URL.DICOM_SENDER_LOGS}/${logId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, logId));
    }
  }
);

// DICOM logs slice
const dicomLogSlice = createSlice({
  name: 'dicomLog',
  initialState,
  reducers: {
    clearDicomLogs(state) {
      state.dicomLogs = [];
      state.selectedLogId = null;
      state.isDicomEventUnlocked = false;
      state.dicomEventDetails = null;
      state.isDicomEventLoading = false;
      state.dicomEventError = null;
    },
    clearDicomEventDetails(state) {
      state.dicomEventDetails = null;
      state.isDicomEventUnlocked = false;
      state.isDicomEventLoading = false;
      state.dicomEventError = null;
      state.selectedLogId = null;
    },
    setSelectedLogId(state, action) {
      state.selectedLogId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDicomLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.dicomLogs = [];
        state.selectedLogId = null;
        state.isDicomEventUnlocked = false;
        state.dicomEventDetails = null;
        state.isDicomEventLoading = false;
        state.dicomEventError = null;
      })
      .addCase(fetchDicomLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.dicomLogs = action.payload;
      })
      .addCase(fetchDicomLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDicomEventDetails.pending, (state) => {
        state.isDicomEventLoading = true;
        state.dicomEventError = null;
      })
      .addCase(fetchDicomEventDetails.fulfilled, (state, action) => {
        state.isDicomEventLoading = false;
        state.dicomEventDetails = action.payload;
        state.isDicomEventUnlocked = true;
      })
      .addCase(fetchDicomEventDetails.rejected, (state, action) => {
        state.isDicomEventLoading = false;
        state.dicomEventError = action.payload;
      });
  },
});

// Export actions and reducer
export const dicomLogActions = dicomLogSlice.actions;
export const dicomLogReducer = dicomLogSlice.reducer;

// Selectors
export const getDicomLogs = (state) => state.dicomLog.dicomLogs;
export const getDicomLogsLength = (state) => state.dicomLog.dicomLogs?.totalCount || 0;
export const getDicomLogsLengthByMrn = (state, mrn) => {
  if (!mrn) return 0;
  const dicomLogs = getDicomLogs(state);
  if (!dicomLogs || dicomLogs.length === 0) return 0;
  return dicomLogs?.listOfLogs?.filter((log) => log.mrn === mrn)?.length || 0;
}
export const getDicomLogsLoading = (state) => state.dicomLog.loading;
export const getDicomLogsError = (state) => state.dicomLog.error;
export const getSelectedLogId = (state) => state.dicomLog.selectedLogId;
export const getSelectedLog = (state, logId) =>
  state.dicomLog.dicomLogs?.listOfLogs?.find((log) => log.id === logId);
export const getDicomEventDetails = (state) => state.dicomLog.dicomEventDetails?.dicomEventLog;
export const getDicomEventLoading = (state) => state.dicomLog.isDicomEventLoading;
export const getDicomEventError = (state) => state.dicomLog.dicomEventError;
export const isDicomEventUnlocked = (state) => state.dicomLog.isDicomEventUnlocked;
